import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import "./UploadFiles.css";
import logo from "../../assets/fishtagged-logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Hamburger from '../components/Hamburger';
import { saveImages, loadImages, clearImages } from "../../imageDB";
import { snapshotCurrentReport, upsertReport } from "../../reportStorage";
import {
  faTrash, faExpand, faCamera, faUpload, faAngleLeft, faAngleRight,
  faTimes, faExclamationTriangle, faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useFileContext } from "../../FileContext";

const UploadFiles = ({ user }) => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const successTimerRef = useRef(null);
  const errorTimerRef = useRef(null);

  const geoPromiseRef = useRef(null);
  // Mirrors images state synchronously so callbacks always see the full list.
  const imagesRef = useRef([]);
  // Only true after the initial IndexedDB load completes — prevents any
  // accidental save with an empty array before images have been restored.
  const loadedRef = useRef(false);

  const { addRawFile, removeRawFile } = useFileContext();

  // Persist the given image list to IndexedDB and update the localStorage
  // report snapshot. Returns a promise so callers can await it before navigating.
  const persistImages = async (allImages) => {
    await saveImages(allImages);
    const activeId = sessionStorage.getItem("activeReportId") || undefined;
    const report = snapshotCurrentReport(activeId, allImages);
    const saved = upsertReport(report);
    if (saved === false) setShowStorageModal(true);
    if (!sessionStorage.getItem("activeReportId")) {
      sessionStorage.setItem("activeReportId", report.id);
    }
  };

  useEffect(() => {
    loadImages().then((imgs) => {
      if (imgs && imgs.length > 0) {
        imagesRef.current = imgs;
        setImages(imgs);
      }
      loadedRef.current = true;
    });
  }, []);

  const showSuccess = (message) => {
    setErrorMessage("");
    setSuccessMessage(message);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => setSuccessMessage(""), 3000);
  };

  const showError = (message) => {
    setSuccessMessage("");
    setErrorMessage(message);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setErrorMessage(""), 4000);
  };

  const handleUploadClick = () => {
    geoPromiseRef.current = null;
    document.getElementById("fileInput")?.click();
  };

  const handleCameraClick = () => {
    geoPromiseRef.current = new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        }),
        () => resolve(null),
        { timeout: 10000, maximumAge: 0 }
      );
    });

    const now = new Date();
    geoPromiseRef.cameraDatetime = {
      date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`,
      time: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
    };

    document.getElementById("cameraInput")?.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const isCamera = !!geoPromiseRef.current;
    const cameraDatetime = isCamera ? (geoPromiseRef.cameraDatetime ?? null) : null;
    const cameraLocation = isCamera ? await geoPromiseRef.current : null;

    let doneCount = 0;

    files.forEach((file) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const id = `${Date.now()}-${Math.random()}`;
        const newImage = {
          id,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: reader.result,
          isCamera,
          cameraLocation: cameraLocation ?? null,
          cameraDatetime: cameraDatetime ?? null,
        };
        addRawFile(id, file);

        // Update ref and state together so both are always in sync
        imagesRef.current = [...imagesRef.current, newImage];
        setImages([...imagesRef.current]);
        doneCount++;

        // Once all files in this batch are read, persist the full list.
        // Using imagesRef.current ensures we have every image, not just
        // what React state has caught up to yet.
        if (doneCount === files.length) {
          persistImages(imagesRef.current);
          showSuccess(files.length === 1 ? `"${files[0].name}" added` : `${files.length} images added`);
        }
      };

      reader.onerror = () => {
        showError(`Upload failed: could not read "${file.name}"`);
      };

      reader.readAsDataURL(file);
    });

    geoPromiseRef.current = null;
    geoPromiseRef.cameraDatetime = null;
    e.target.value = "";
  };

  const handleTrashClick = (id) => setDeleteId(id);

  const handleDeleteConfirm = () => {
    removeRawFile(deleteId);
    const updated = imagesRef.current.filter((img) => img.id !== deleteId);
    imagesRef.current = updated;
    setImages(updated);
    setDeleteId(null);

    if (updated.length === 0) {
      clearImages();
      sessionStorage.removeItem("reportImageData");
      sessionStorage.removeItem("fishInfoData");
      const activeId = sessionStorage.getItem("activeReportId") || undefined;
      if (activeId) upsertReport(snapshotCurrentReport(activeId, []));
    } else {
      persistImages(updated);
    }
  };

  const handleNext = async () => {
    // Guard: don't navigate if no images
    if (imagesRef.current.length === 0) {
      const imgs = await loadImages();
      if (!imgs || imgs.length === 0) { setShowUploadModal(true); return; }
    }
    // Await the persist so IndexedDB write finishes before ReportImage loads
    await persistImages(imagesRef.current);
    navigate("/reportImage");
  };

  return (
    <>
      <div className="bg-image" />
      <div className="top-row">
        <div className="logo"><img src={logo} alt="FishTagged Logo" /></div>
        <div className="top-hamburger"><Hamburger user={user} /></div>
      </div>
      <div className="uf-viewport">
        <div className="uf-content">
          <div className="card-wrapper">
            <div className="uf-card">
              <p className="uf-step-label">New entry (2 out of 5)</p>
              <h1 className="uf-title">Upload Images</h1>
              <p className="uf-subtitle">Provide at least one image of the fish.</p>
              <div className="uf-button-row">
                <button className="uf-primary-btn" onClick={handleCameraClick}>
                  <FontAwesomeIcon icon={faCamera} /> Take photo
                </button>
                <button className="uf-secondary-btn" onClick={handleUploadClick}>
                  <FontAwesomeIcon icon={faUpload} /> Upload
                </button>
                <input
                  type="file"
                  id="cameraInput"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                  ref={(el) => {
                    if (el) {
                      if (navigator.maxTouchPoints > 0) {
                        el.setAttribute("capture", "environment");
                      } else {
                        el.removeAttribute("capture");
                      }
                    }
                  }}
                />
                <input type="file" id="fileInput" hidden multiple accept="image/*" onChange={handleFileChange} />
              </div>

              {successMessage && (
                <div className="uf-success-message">
                  <FontAwesomeIcon icon={faCheckCircle} className="uf-success-icon" />
                  <span>{successMessage}</span>
                </div>
              )}

              {errorMessage && (
                <div className="uf-error-message">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="uf-error-icon" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {images.length > 0 && (
                <div className="uf-image-list">
                  {images.map((img) => (
                    <div className="uf-image-item" key={img.id}>
                      <div className="uf-image-thumb" style={{ backgroundImage: `url(${img.preview})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                      <div className="uf-image-info">
                        <p>{(img.size / 1024 / 1024).toFixed(2)} MB</p>
                        <p>{img.name}</p>
                      </div>
                      <div className="uf-image-actions">
                        <FontAwesomeIcon icon={faExpand} onClick={() => setZoomImage(img.preview)} />
                        <FontAwesomeIcon icon={faTrash} onClick={() => handleTrashClick(img.id)} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bottom-nav">
              <FontAwesomeIcon icon={faAngleLeft} className="arrow" onClick={() => navigate("/anonymity")} />
              <FontAwesomeIcon icon={faAngleRight} className="arrow" onClick={handleNext} />
            </div>
          </div>
        </div>
      </div>

      {deleteId && (
        <div className="uf-modal-overlay">
          <div className="uf-modal">
            <FontAwesomeIcon icon={faTimes} className="uf-close-icon" onClick={() => setDeleteId(null)} />
            <FontAwesomeIcon icon={faExclamationTriangle} className="uf-warning-icon" />
            <h2>Delete image?</h2>
            <p>This process cannot be undone, and you will have to reupload.</p>
            <div className="uf-modal-buttons">
              <button className="uf-secondary-btn" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="uf-primary-btn" onClick={handleDeleteConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {zoomImage && (
        <div className="uf-zoom-overlay">
          <div className="uf-zoom-modal">
            <FontAwesomeIcon icon={faTimes} className="uf-close-icon" onClick={() => setZoomImage(null)} />
            <img src={zoomImage} alt="Preview" />
          </div>
        </div>
      )}
      {showUploadModal && (
        <div className="uf-modal-overlay">
          <div className="uf-modal">
            <FontAwesomeIcon icon={faTimes} className="uf-close-icon" onClick={() => setShowUploadModal(false)} />
            <FontAwesomeIcon icon={faExclamationTriangle} className="uf-warning-icon" />
            <h2>Image Required</h2>
            <p>Please upload at least one image before continuing.</p>
            <div className="uf-modal-buttons">
              <button className="uf-primary-btn" onClick={() => setShowUploadModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}
      {showStorageModal && (
        <div className="uf-modal-overlay">
          <div className="uf-modal">
            <FontAwesomeIcon icon={faTimes} className="uf-close-icon" onClick={() => setShowStorageModal(false)} />
            <FontAwesomeIcon icon={faExclamationTriangle} className="uf-warning-icon" />
            <h2>Storage Full</h2>
            <p>Your device storage is full. Some report progress could not be saved. Please submit or delete existing reports to free up space.</p>
            <div className="uf-modal-buttons">
              <button className="uf-primary-btn" onClick={() => setShowStorageModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadFiles;